import {
	axiosErrorHandler,
	get,
	post,
	put,
	deletes,
} from 'models/shared/helpers/axiosInstance';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import { buildPathWithQueryParams } from 'models/shared/helpers/utils';
import { getUser } from 'localStorage/authStorage';
import {
	CreateSauronConversationDto,
	UpdateSauronConversationDto,
	QuerySauronConversationDto,
	SauronConversation,
	SauronConversationStats,
	SauronWebhookRequestDto,
} from '../interfaces/sauron-conversation.interface';

const baseSauronConversationPath = '/v1/sauron-conversations';

export const createSauronConversation = async (
	createDto: CreateSauronConversationDto,
): Promise<SauronConversation> => {
	try {
		const res = await post<SauronConversation>(
			baseSauronConversationPath,
			createDto,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversations = async (
	query: QuerySauronConversationDto,
): Promise<PaginatedDocuments<SauronConversation>> => {
	try {
		const res = await get<PaginatedDocuments<SauronConversation>>(
			buildPathWithQueryParams(baseSauronConversationPath, query),
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversationStats =
	async (): Promise<SauronConversationStats> => {
		try {
			const url = `${baseSauronConversationPath}/stats`;
			const res = await get<SauronConversationStats>(url);
			return res.data;
		} catch (err) {
			throw axiosErrorHandler(err);
		}
	};

export const fetchSauronConversationsBySessionId = async (
	sessionId: string,
): Promise<SauronConversation[]> => {
	try {
		const url = `${baseSauronConversationPath}/session/${sessionId}`;
		const res = await get<SauronConversation[]>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversationsBySender = async (
	sender: string,
): Promise<SauronConversation[]> => {
	try {
		const url = `${baseSauronConversationPath}/sender/${sender}`;
		const res = await get<SauronConversation[]>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversation = async (
	id: string,
): Promise<SauronConversation> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		const res = await get<SauronConversation>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const updateSauronConversation = async (
	id: string,
	updateDto: UpdateSauronConversationDto,
): Promise<SauronConversation> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		const res = await put<SauronConversation>(url, updateDto);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const deleteSauronConversation = async (id: string): Promise<void> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		await deletes(url);
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const deleteSauronConversationsBySessionId = async (
	sessionId: string,
): Promise<void> => {
	try {
		const url = `${baseSauronConversationPath}/session/${sessionId}`;
		await deletes(url);
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const callSauronChat = async (
	webhookData: Omit<SauronWebhookRequestDto, 'domain' | 'user'>,
): Promise<SauronConversation> => {
	try {
		const domain =
			process.env.REACT_APP_API_BASE_URL ||
			'https://api-pandaboard.dev.ozon.mobi/';

		const requestBody: SauronWebhookRequestDto = {
			...webhookData,
			domain,
			user: getUser()?._id || '',
		};

		const url = `${baseSauronConversationPath}/webhook`;
		const res = await post<SauronConversation>(url, requestBody);

		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
